import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AuthLanguageDropdown from "../Components/AuthLanguageDropdown";
import AuthLeftColumn from "../Components/AuthLeftColumn";
import { vendorSignUpAction } from "../actions/auth";
import { isValidEmailAddress } from "../utils/formValidation";
import { useTranslation } from "../i18n";
import "bootstrap/dist/css/bootstrap-grid.min.css";
import "./Signup.css";
import { countryCodes } from "../data/countryCodes";
import {
  handleNumericInputKeyDown,
  sanitizeNumericInput,
} from "../utils/numericInput";

const PHONE_COUNTRY_OPTIONS = countryCodes
  .filter((country) => country?.code && country?.name && country?.dial_code)
  .map((country) => ({
    code: country.code.toUpperCase(),
    name: country.name,
    dialCode: country.dial_code,
    searchText: [country.name, country.code, country.dial_code]
      .join(" ")
      .toLowerCase(),
  }))
  .filter((country) => /^[A-Z]{2}$/.test(country.code))
  .sort((first, second) => first.name.localeCompare(second.name));

function SearchableCountryDropdown({ value, options, onChange, label }) {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((country) => country.code === value);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = useMemo(
    () =>
      normalizedQuery
        ? options.filter((country) => country.searchText.includes(normalizedQuery))
        : options,
    [normalizedQuery, options],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  const openMenu = () => {
    setQuery("");
    setIsOpen(true);
  };

  const selectCountry = (country) => {
    onChange(country.code);
    setIsOpen(false);
    setQuery("");
  };

  const handleOptionKeyDown = (event, index) => {
    const optionButtons = rootRef.current?.querySelectorAll("[role='option']");
    if (event.key === "Escape") {
      setIsOpen(false);
      rootRef.current?.querySelector("[role='combobox']")?.focus();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      optionButtons?.[Math.min(index + 1, optionButtons.length - 1)]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (index === 0) searchRef.current?.focus();
      else optionButtons?.[index - 1]?.focus();
    }
  };

  return (
    <div
      ref={rootRef}
      className={"vendor-searchable-country vendor-searchable-country--dial-code" + (isOpen ? " is-open" : "")}
    >
      <button
        type="button"
        className="vendor-searchable-country-trigger"
        role="combobox"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="signup-phone-country-options"
        onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !isOpen) {
            event.preventDefault();
            openMenu();
          }
        }}
      >
        {selected ? (
          <img
            className="vendor-searchable-country-flag"
            src={"https://flagcdn.com/24x18/" + selected.code.toLowerCase() + ".png"}
            alt={selected.code}
          />
        ) : null}
        <span className="vendor-searchable-country-value">
          {selected?.dialCode || "+91"}
        </span>
        <svg className="vendor-searchable-country-chevron" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="m5.5 7.5 4.5 4.5 4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div className="vendor-searchable-country-menu" id="signup-phone-country-options">
          <div className="vendor-searchable-country-search">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <circle cx="8.8" cy="8.8" r="5.8" stroke="currentColor" strokeWidth="1.6" />
              <path d="m13.2 13.2 3.6 3.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <input
              ref={searchRef}
              type="search"
              value={query}
              placeholder="Search countries"
              aria-label={"Search " + label.toLowerCase()}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setIsOpen(false);
                  rootRef.current?.querySelector("[role='combobox']")?.focus();
                } else if (event.key === "ArrowDown") {
                  event.preventDefault();
                  rootRef.current?.querySelector("[role='option']")?.focus();
                }
              }}
            />
          </div>
          <div className="vendor-searchable-country-options" role="listbox" aria-label={label}>
            {filteredOptions.length ? (
              filteredOptions.map((country, index) => (
                <button
                  key={country.code}
                  type="button"
                  className={"vendor-searchable-country-option" + (country.code === value ? " is-selected" : "")}
                  role="option"
                  aria-selected={country.code === value}
                  onClick={() => selectCountry(country)}
                  onKeyDown={(event) => handleOptionKeyDown(event, index)}
                >
                  <img
                    className="vendor-searchable-country-option-flag"
                    src={"https://flagcdn.com/24x18/" + country.code.toLowerCase() + ".png"}
                    alt={country.code}
                  />
                  <span className="vendor-searchable-country-option-name">{country.name}</span>
                  <span className="vendor-searchable-country-option-code">{country.dialCode}</span>
                </button>
              ))
            ) : (
              <p className="vendor-searchable-country-empty">No countries found.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FieldIcon({ name }) {
  const paths = {
    user: (
      <>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M4.5 20v-1.5a7.5 7.5 0 0 1 15 0V20" />
      </>
    ),
    mail: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="1.7" />
        <path d="m4 7 8 6 8-6" />
      </>
    ),
  };

  return (
    <svg className="login-input-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

export default function Signup() {
  const { text, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const signupLoading = useSelector((state) => Boolean(state.auth?.vendorSignupLoading));
  const signupError = useSelector((state) => state.auth?.vendorSignupError || "");
  const initialFormData = location.state?.formData || {};
  const defaultPhoneCountry =
    PHONE_COUNTRY_OPTIONS.find((country) => country.dialCode === "+91")?.code ||
    PHONE_COUNTRY_OPTIONS[0]?.code ||
    "";

  const [form, setForm] = useState(() => ({
    name: initialFormData.name || initialFormData["contact-person"] || "",
    email: initialFormData.email || initialFormData["company-email"] || "",
    phone: sanitizeNumericInput(
      initialFormData.phone || initialFormData.telephone || "",
      { allowDecimal: false },
    ),
    phoneCountry:
      initialFormData.phoneCountry ||
      initialFormData["telephone-country"] ||
      defaultPhoneCountry,
    password: "",
  }));
  const [showPassword, setShowPassword] = useState(false);

  const canSubmitSignup =
    form.name.trim().length > 0 &&
    isValidEmailAddress(form.email) &&
    form.phone.length > 0 &&
    Boolean(form.phoneCountry) &&
    form.password.length > 0 &&
    !signupLoading;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({
      ...previous,
      [name]:
        name === "phone"
          ? sanitizeNumericInput(value, { allowDecimal: false })
          : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const phoneCountry = PHONE_COUNTRY_OPTIONS.find(
      (country) => country.code === form.phoneCountry,
    );
    const payload = {
      contactPerson: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.replace(/\D/g, ""),
      countryCode: phoneCountry?.dialCode || "+91",
      password: form.password,
    };
    const result = await dispatch(vendorSignUpAction(payload));
    if (!result?.success) return;

    const pendingSignup = {
      email: payload.email,
      phone: [payload.countryCode, payload.phone].filter(Boolean).join(" "),
      formData: {
        name: form.name,
        email: form.email,
        phone: form.phone,
        phoneCountry: form.phoneCountry,
      },
    };

    sessionStorage.removeItem("forgotPasswordUser");
    sessionStorage.removeItem("forgotPasswordOtp");
    sessionStorage.setItem("vendorSignupPending", JSON.stringify(pendingSignup));
    navigate("/verify-otp", { state: { flow: "vendor-signup" } });
  };

  const openLogin = () => navigate("/");

  return (
    <main className="admin-login-page admin-login-page--reference admin-login-page--signup admin-login-page--auth-shared">
      <div className="container-fluid p-0">
        <div className="row g-0 admin-login-card">
          <AuthLeftColumn />

          <div className="col-md-6 col-12 admin-login-right">
            <div className="admin-login-right-inner">
              <div className="admin-login-mobile-logo d-md-none">
                <img src="/assets/lualilogoblack.svg" alt={t("ui.luali.commodities")} />
                <AuthLanguageDropdown />
              </div>

              <div className="admin-login-form-content">
                <div className="admin-login-heading">
                  <h1>{text("Create your vendor account")}</h1>
                  <p>{text("Enter your details to get started.")}</p>
                </div>

                <form className="admin-login-form" onSubmit={handleSubmit}>
                  <div className="admin-login-field">
                    <label htmlFor="signup-name" className="form-label">{text("Name")}</label>
                    <div className="login-input-box">
                      <FieldIcon name="user" />
                      <input
                        id="signup-name"
                        type="text"
                        name="name"
                        className="form-control"
                        placeholder={text("Enter your name")}
                        value={form.name}
                        onChange={handleChange}
                        autoComplete="name"
                        required
                      />
                    </div>
                  </div>

                  <div className="admin-login-field">
                    <label htmlFor="signup-email" className="form-label">{t("ui.email.address")}</label>
                    <div className="login-input-box">
                      <FieldIcon name="mail" />
                      <input
                        id="signup-email"
                        type="email"
                        name="email"
                        className="form-control"
                        placeholder={text("Enter your email address")}
                        value={form.email}
                        onChange={handleChange}
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>

                  <div className="admin-login-field">
                    <label htmlFor="signup-phone" className="form-label">{text("Phone number")}</label>
                    <div className="login-input-box signup-phone-input-box">
                      <SearchableCountryDropdown
                        value={form.phoneCountry}
                        options={PHONE_COUNTRY_OPTIONS}
                        onChange={(phoneCountry) => setForm((previous) => ({ ...previous, phoneCountry }))}
                        label="Phone country code"
                      />
                      <input
                        id="signup-phone"
                        type="tel"
                        name="phone"
                        className="form-control"
                        placeholder={text("Enter your phone number")}
                        value={form.phone}
                        onChange={handleChange}
                        onKeyDown={(event) =>
                          handleNumericInputKeyDown(event, { allowDecimal: false })
                        }
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="tel-national"
                        required
                      />
                    </div>
                  </div>

                  <div className="admin-login-field">
                    <label htmlFor="signup-password" className="form-label">{t("ui.password")}</label>
                    <div className="login-input-box">
                      <svg className="login-input-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="5" y="10" width="14" height="11" rx="2" />
                        <path d="M8 10V7a4 4 0 1 1 8 0v3M12 14.5v2.5" />
                      </svg>
                      <input
                        id="signup-password"
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className="form-control"
                        placeholder={text("Create a password")}
                        value={form.password}
                        onChange={handleChange}
                        autoComplete="new-password"
                        required
                      />
                      <button
                        type="button"
                        className="login-show-password"
                        onClick={() => setShowPassword((visible) => !visible)}
                        aria-label={showPassword ? t("ui.hidePassword") : t("ui.showPassword")}
                        aria-pressed={showPassword}
                      >
                        {showPassword ? (
                          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M3 3 21 21" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                            <path d="M10.58 10.58A2 2 0 0 0 13.42 13.42M9.88 5.08A10 10 0 0 1 12 4.85c4.45 0 7.67 2.58 9.3 7.15a12 12 0 0 1-2.24 3.86M6.61 6.61C4.82 7.77 3.5 9.57 2.7 12c1.63 4.57 4.85 7.15 9.3 7.15 1.47 0 2.8-.28 3.97-.81" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M2.7 12C4.33 7.43 7.55 4.85 12 4.85c4.45 0 7.67 2.58 9.3 7.15-1.63 4.57-4.85 7.15-9.3 7.15-4.45 0-7.67-2.58-9.3-7.15Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.7" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {signupError ? (
                    <div className="admin-login-error" role="alert">{text(signupError)}</div>
                  ) : null}
                  <button type="submit" className="recent-view-all-btn w-100" disabled={!canSubmitSignup}>
                    <span>{signupLoading ? text("Creating account...") : text("Create Account")}</span>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M4 12h15m-6-6 6 6-6 6" />
                    </svg>
                  </button>
                </form>

                <div className="admin-login-bottom-text">
                  <span>{text("Already have an account?")}</span>
                  <button type="button" onClick={openLogin}>{text("Sign in")}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}